import { useId, useMemo, useRef } from "react"

import { useOptionalFormContext } from "shared/ui/form/FormContext"

import { InputContextType, InputContextUserProps } from "../Input"
import { useInputDescriptionManager } from "../accessibility/useInputDescriptionManager"

export interface UseInputContextProps extends InputContextUserProps {}

export function useInputContext({
  name,
  label,
  readonlyLabel,
  id: propsID,
  readonly,
  required,
  loading,
  disabled,
  autoFocus,
  hasError,
  hasWarning,
  unit
}: UseInputContextProps) {
  const internalID = useId()
  const id = propsID ?? internalID
  const inputRef = useRef<HTMLElement>(null)

  const formContext = useOptionalFormContext()
  // A form-level disable (e.g. an in-flight submit) reaches every input through
  // FormContext; an explicit per-input prop still wins over it.
  const isDisabled = disabled ?? formContext?.isDisabled

  const { descriptionIDs, registerDescription, unregisterDescription } =
    useInputDescriptionManager()

  const inputContext = useMemo<InputContextType>(
    () => ({
      id,
      name: name ?? "",
      label: label ?? "",
      readonlyLabel: readonlyLabel ?? label ?? "",
      readonly,
      required,
      loading,
      disabled: isDisabled,
      autoFocus,
      hasError,
      hasWarning,
      unit,
      inputRef,
      descriptionIDs,
      registerDescription,
      unregisterDescription
    }),
    [
      id,
      name,
      label,
      readonlyLabel,
      readonly,
      required,
      loading,
      isDisabled,
      autoFocus,
      hasError,
      hasWarning,
      unit,
      descriptionIDs,
      registerDescription,
      unregisterDescription
    ]
  )

  return { inputContext }
}
